import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { onboardFunction } from "shared/api/function_data";
import "./OnboardPage.css";

const FUNCTION_TYPES = [
  {
    value: "WBR",
    label: "WBR",
    full: "Weekly Business Review",
    icon: "📅",
    desc: "Weekly cadence reporting & metrics tracking",
  },
  {
    value: "MBR",
    label: "MBR",
    full: "Monthly Business Review",
    icon: "📊",
    desc: "Monthly deep-dive analytics & trends",
  },
  {
    value: "QBR",
    label: "QBR",
    full: "Quarterly Business Review",
    icon: "📈",
    desc: "Quarterly strategic review & planning",
  },
];

// ✅ REMOVED: const YEARS = ...

const INITIAL_FORM = {
  functionName: "",
  functionType: "",
  startYear: "2026",        // ✅ DEFAULT VALUE — always sent as 2026
  shortName: "",
  adminLogin: "",
  businessUseCase: "",
};

const STEPS = [
  { id: 1, title: "Function Identity", icon: "🏷️" },
  { id: 2, title: "Configuration", icon: "⚙️" },
  { id: 3, title: "Review & Submit", icon: "🚀" },
];

const faqs = [
  {
    question: "How long does the onboarding process take?",
    answer:
      "Typically 5–10 business days from submission to Go Live, depending on complexity and UAT feedback cycles.",
  },
  {
    question: "Who can request a new Function onboarding?",
    answer:
      "Any team lead or manager within SWOTS functions can initiate an onboarding request through this portal.",
  },
  {
    question: "What is River Function?",
    answer:
      "River Function is the production analytics environment within PULSE where all approved Functions are deployed, monitored, and maintained.",
  },
  {
    question: "Can I modify my Function after going live?",
    answer:
      "Yes. Post-launch modifications can be requested through the PULSE Admin panel or by contacting the CIA team.",
  },
  {
    question: "What happens after I submit the form?",
    answer:
      "Your submission enters a review queue. The CIA team validates your details, checks data source compatibility, and begins the configuration process.",
  },
];

function OnboardPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState("light");
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [activeFaq, setActiveFaq] = useState(null);
  const formRef = useRef(null);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  const toggleFaq = (index) =>
    setActiveFaq(activeFaq === index ? null : index);

  // ── Form Helpers ──
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    if (step === 1) {
      if (!form.functionName.trim())
        newErrors.functionName = "Function name is required";
      else if (form.functionName.trim().length < 3)
        newErrors.functionName = "Must be at least 3 characters";
      if (!form.functionType)
        newErrors.functionType = "Please select a function type";
      if (!form.shortName.trim())
        newErrors.shortName = "Short name is required";
      else if (form.shortName.trim().length > 10)
        newErrors.shortName = "Max 10 characters allowed";
      else if (!/^[A-Za-z0-9_-]+$/.test(form.shortName.trim()))
        newErrors.shortName = "Only letters, numbers, hyphens, underscores";
    }
    if (step === 2) {
      // ✅ REMOVED: startYear validation (always 2026)
      if (!form.adminLogin.trim())
        newErrors.adminLogin = "Admin login alias is required";
      else if (!/^[a-z][a-z0-9_-]{2,}$/i.test(form.adminLogin.trim()))
        newErrors.adminLogin =
          "Invalid alias (min 3 chars, start with letter)";
      if (!form.businessUseCase.trim())
        newErrors.businessUseCase = "Business use case is required";
      else if (form.businessUseCase.trim().length < 20)
        newErrors.businessUseCase = "Please provide at least 20 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
      scrollToForm();
    }
  };

  const goBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    scrollToForm();
  };

  const scrollToForm = () => {
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    const payload = {
      function_name: form.functionName.trim(),
      function_type: form.functionType,
      start_year: parseInt(form.startYear, 10),   // ✅ Always sends 2026
      short_name: form.shortName.trim().toUpperCase(),
      admin_login: form.adminLogin.trim().toLowerCase(),
      business_use_case: form.businessUseCase.trim(),
      submitted_at: new Date().toISOString(),
    };

    try {
      // Goes through the standard authenticated API path (Cognito Bearer),
      // the same authorizer used by every other endpoint.
      await onboardFunction(payload);
      setSubmitStatus("success");
    } catch (err) {
      console.error("Onboard submission error:", err);
      setSubmitError(err.message || "Submission failed. Please try again.");
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setTouched({});
    setCurrentStep(1);
    setSubmitStatus(null);
    setSubmitError(null);
    scrollToForm();
  };

  const selectedType = FUNCTION_TYPES.find(
    (t) => t.value === form.functionType
  );

  const getCompletionPercent = () => {
    // ✅ Exclude startYear from completion calc since it's auto-filled
    const trackFields = [
      form.functionName,
      form.functionType,
      form.shortName,
      form.adminLogin,
      form.businessUseCase,
    ];
    const filled = trackFields.filter((v) => v.trim() !== "").length;
    return Math.round((filled / trackFields.length) * 100);
  };

  // ── Render ──
  return (
    <div className={`onboard-page ${theme}`}>
      {/* ─── Navbar ─── */}
      <nav className="onboard-navbar">
        <div className="onboard-navbar-inner">
          <div className="onboard-brand" onClick={() => navigate("/")}>
            <span className="onboard-brand-mark">PULSE</span>
            <span className="onboard-brand-text">Onboarding</span>
          </div>
          <div className="onboard-navbar-actions">
            <button className="onboard-theme-toggle" onClick={toggleTheme}>
              <span className="theme-toggle__icon">
                {theme === "dark" ? "☀️" : "🌙"}
              </span>
              {theme === "dark" ? "Light" : "Dark"}
            </button>
            <button
              className="onboard-back-btn"
              onClick={() => navigate("/")}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      <main className="onboard-main">
        {/* ─── Hero ─── */}
        <section className="onboard-hero">
          <div className="onboard-hero-badge">🛰️ FUNCTION ONBOARDING</div>
          <h1 className="onboard-hero-title">
            Register Your{" "}
            <span className="onboard-highlight">Function</span> on PULSE
          </h1>
          <p className="onboard-hero-subtitle">
            Complete the form below to onboard your function into the PULSE
            ecosystem. Our team will review your submission and configure
            everything for River Function deployment.
          </p>
          <div className="onboard-hero-actions">
            <button
              className="onboard-cta-primary"
              onClick={() => {
                document
                  .getElementById("form-section")
                  .scrollIntoView({ behavior: "smooth" });
              }}
            >
              📝 Fill Onboarding Form
            </button>
            <button
              className="onboard-cta-secondary"
              onClick={() =>
                document
                  .getElementById("faq-section")
                  .scrollIntoView({ behavior: "smooth" })
              }
            >
              Have Questions? ↓
            </button>
          </div>

          <div className="onboard-info-cards">
            <div className="onboard-info-card">
              <span className="info-icon">⏱️</span>
              <div>
                <strong>5–10 Days</strong>
                <p>Average onboarding time</p>
              </div>
            </div>
            <div className="onboard-info-card">
              <span className="info-icon">📋</span>
              <div>
                <strong>3 Steps</strong>
                <p>Quick guided form</p>
              </div>
            </div>
            <div className="onboard-info-card">
              <span className="info-icon">🎯</span>
              <div>
                <strong>River Function</strong>
                <p>Production deployment</p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Form Section ─── */}
        <section
          className="onboard-form-section"
          id="form-section"
          ref={formRef}
        >
          <h2 className="onboard-section-title centered">
            Onboarding <span className="onboard-highlight">Form</span>
          </h2>
          <p className="onboard-section-subtitle">
            Fill in your function details — all fields are required
          </p>

          {/* Step Progress */}
          <div className="form-stepper">
            {STEPS.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div
                  className={`stepper-step ${
                    currentStep === step.id ? "active" : ""
                  } ${currentStep > step.id ? "completed" : ""} ${
                    submitStatus === "success" ? "all-done" : ""
                  }`}
                >
                  <div className="stepper-circle">
                    {currentStep > step.id || submitStatus === "success" ? (
                      <span className="stepper-check">✓</span>
                    ) : (
                      <span className="stepper-icon">{step.icon}</span>
                    )}
                  </div>
                  <span className="stepper-label">{step.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`stepper-line ${
                      currentStep > step.id ? "filled" : ""
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Form Container */}
          <div className="form-container">
            {submitStatus === "success" ? (
              /* ── Success State ── */
              <div className="form-success">
                <div className="success-icon-wrap">
                  <span className="success-icon">🎉</span>
                </div>
                <h3>Function Submitted Successfully!</h3>
                <p>
                  Your function <strong>"{form.functionName}"</strong> (
                  {form.shortName.toUpperCase()}) has been submitted for review.
                  The CIA team will process your request within 2 business days.
                </p>
                <div className="success-summary">
                  <div className="summary-row">
                    <span className="summary-label">Function</span>
                    <span className="summary-value">{form.functionName}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Type</span>
                    <span className="summary-value">
                      {selectedType?.full} ({form.functionType})
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Short Name</span>
                    <span className="summary-value">
                      {form.shortName.toUpperCase()}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Admin</span>
                    <span className="summary-value">
                      {form.adminLogin.toLowerCase()}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Start Year</span>
                    <span className="summary-value">2026</span>
                  </div>
                </div>
                <div className="success-actions">
                  <button
                    className="onboard-cta-primary"
                    onClick={handleReset}
                  >
                    📝 Submit Another Function
                  </button>
                  <button
                    className="onboard-cta-secondary"
                    onClick={() => navigate("/")}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            ) : (
              /* ── Form Body ── */
              <div className="form-body">
                <div className="form-fields">
                  {/* STEP 1 */}
                  {currentStep === 1 && (
                    <div className="form-step-content">
                      <div className="step-header">
                        <span className="step-header-icon">🏷️</span>
                        <div>
                          <h3>Function Identity</h3>
                          <p>
                            Define your function's name, type, and identifier
                          </p>
                        </div>
                      </div>

                      {/* Function Name */}
                      <div
                        className={`form-group ${
                          errors.functionName && touched.functionName
                            ? "has-error"
                            : ""
                        } ${
                          !errors.functionName &&
                          touched.functionName &&
                          form.functionName.trim().length >= 3
                            ? "is-valid"
                            : ""
                        }`}
                      >
                        <label className="form-label">
                          Function Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="e.g., Customer Experience Analytics"
                          value={form.functionName}
                          onChange={(e) =>
                            updateField("functionName", e.target.value)
                          }
                          onBlur={() =>
                            setTouched((p) => ({ ...p, functionName: true }))
                          }
                        />
                        <span className="form-hint">
                          Full descriptive name of your function
                        </span>
                        {errors.functionName && touched.functionName && (
                          <span className="form-error">
                            {errors.functionName}
                          </span>
                        )}
                      </div>

                      {/* Function Type */}
                      <div
                        className={`form-group ${
                          errors.functionType ? "has-error" : ""
                        }`}
                      >
                        <label className="form-label">
                          Function Type <span className="required">*</span>
                        </label>
                        <div className="type-cards">
                          {FUNCTION_TYPES.map((type) => (
                            <div
                              key={type.value}
                              className={`type-card ${
                                form.functionType === type.value
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                updateField("functionType", type.value)
                              }
                            >
                              <span className="type-card-icon">
                                {type.icon}
                              </span>
                              <div className="type-card-info">
                                <strong>{type.label}</strong>
                                <span className="type-card-full">
                                  {type.full}
                                </span>
                                <p>{type.desc}</p>
                              </div>
                              {form.functionType === type.value && (
                                <span className="type-card-check">✓</span>
                              )}
                            </div>
                          ))}
                        </div>
                        {errors.functionType && (
                          <span className="form-error">
                            {errors.functionType}
                          </span>
                        )}
                      </div>

                      {/* Short Name */}
                      <div
                        className={`form-group ${
                          errors.shortName && touched.shortName
                            ? "has-error"
                            : ""
                        } ${
                          !errors.shortName &&
                          touched.shortName &&
                          form.shortName.trim()
                            ? "is-valid"
                            : ""
                        }`}
                      >
                        <label className="form-label">
                          Function Short Name{" "}
                          <span className="required">*</span>
                        </label>
                        <div className="input-with-counter">
                          <input
                            type="text"
                            className="form-input"
                            placeholder="e.g., CX-ANLYT"
                            value={form.shortName}
                            maxLength={10}
                            onChange={(e) =>
                              updateField(
                                "shortName",
                                e.target.value.toUpperCase()
                              )
                            }
                            onBlur={() =>
                              setTouched((p) => ({ ...p, shortName: true }))
                            }
                          />
                          <span className="char-counter">
                            {form.shortName.length}/10
                          </span>
                        </div>
                        <span className="form-hint">
                          Unique identifier (letters, numbers, hyphens only)
                        </span>
                        {errors.shortName && touched.shortName && (
                          <span className="form-error">
                            {errors.shortName}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ✅ STEP 2 — Year picker REMOVED */}
                  {currentStep === 2 && (
                    <div className="form-step-content">
                      <div className="step-header">
                        <span className="step-header-icon">⚙️</span>
                        <div>
                          <h3>Configuration Details</h3>
                          <p>
                            Set the admin and describe your use case
                          </p>
                        </div>
                      </div>

                      {/* Admin Login */}
                      <div
                        className={`form-group ${
                          errors.adminLogin && touched.adminLogin
                            ? "has-error"
                            : ""
                        } ${
                          !errors.adminLogin &&
                          touched.adminLogin &&
                          form.adminLogin.trim()
                            ? "is-valid"
                            : ""
                        }`}
                      >
                        <label className="form-label">
                          Admin Login Alias{" "}
                          <span className="required">*</span>
                        </label>
                        <div className="input-with-prefix">
                          <span className="input-prefix">@</span>
                          <input
                            type="text"
                            className="form-input prefixed"
                            placeholder="e.g., jsmith"
                            value={form.adminLogin}
                            onChange={(e) =>
                              updateField(
                                "adminLogin",
                                e.target.value.toLowerCase()
                              )
                            }
                            onBlur={() =>
                              setTouched((p) => ({ ...p, adminLogin: true }))
                            }
                          />
                        </div>
                        <span className="form-hint">
                          Primary admin responsible for this function
                        </span>
                        {errors.adminLogin && touched.adminLogin && (
                          <span className="form-error">
                            {errors.adminLogin}
                          </span>
                        )}
                      </div>

                      {/* Business Use Case */}
                      <div
                        className={`form-group ${
                          errors.businessUseCase && touched.businessUseCase
                            ? "has-error"
                            : ""
                        } ${
                          !errors.businessUseCase &&
                          touched.businessUseCase &&
                          form.businessUseCase.trim().length >= 20
                            ? "is-valid"
                            : ""
                        }`}
                      >
                        <label className="form-label">
                          Business Use Case{" "}
                          <span className="required">*</span>
                        </label>
                        <textarea
                          className="form-textarea"
                          rows={5}
                          placeholder="Describe the primary business use case for this function. What metrics will it track? What decisions will it support?"
                          value={form.businessUseCase}
                          onChange={(e) =>
                            updateField("businessUseCase", e.target.value)
                          }
                          onBlur={() =>
                            setTouched((p) => ({
                              ...p,
                              businessUseCase: true,
                            }))
                          }
                        />
                        <div className="textarea-footer">
                          <span className="form-hint">
                            Minimum 20 characters
                          </span>
                          <span className="char-counter">
                            {form.businessUseCase.length} chars
                          </span>
                        </div>
                        {errors.businessUseCase && touched.businessUseCase && (
                          <span className="form-error">
                            {errors.businessUseCase}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 3 — Review */}
                  {currentStep === 3 && (
                    <div className="form-step-content">
                      <div className="step-header">
                        <span className="step-header-icon">🚀</span>
                        <div>
                          <h3>Review & Submit</h3>
                          <p>
                            Verify all details before submitting your function
                          </p>
                        </div>
                      </div>

                      <div className="review-card">
                        <div className="review-section">
                          <h4 className="review-section-title">
                            <span>🏷️</span> Function Identity
                          </h4>
                          <div className="review-grid">
                            <div className="review-item">
                              <span className="review-label">
                                Function Name
                              </span>
                              <span className="review-value">
                                {form.functionName}
                              </span>
                            </div>
                            <div className="review-item">
                              <span className="review-label">Type</span>
                              <span className="review-value">
                                <span className="review-badge type-badge">
                                  {selectedType?.icon} {form.functionType}
                                </span>
                                <span className="review-sub">
                                  {selectedType?.full}
                                </span>
                              </span>
                            </div>
                            <div className="review-item">
                              <span className="review-label">Short Name</span>
                              <span className="review-value mono">
                                {form.shortName.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="review-divider" />

                        <div className="review-section">
                          <h4 className="review-section-title">
                            <span>⚙️</span> Configuration
                          </h4>
                          <div className="review-grid">
                            <div className="review-item">
                              <span className="review-label">Start Year</span>
                              <span className="review-value">2026</span>
                            </div>
                            <div className="review-item">
                              <span className="review-label">Admin Login</span>
                              <span className="review-value mono">
                                @{form.adminLogin.toLowerCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="review-divider" />

                        <div className="review-section">
                          <h4 className="review-section-title">
                            <span>💼</span> Business Use Case
                          </h4>
                          <p className="review-usecase">
                            {form.businessUseCase}
                          </p>
                        </div>
                      </div>

                      {submitStatus === "error" && (
                        <div className="form-error-banner">
                          <span>⚠️</span>
                          {submitError ||
                            "Submission failed. Please try again or contact the CIA team."}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="form-nav">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        className="form-btn-back"
                        onClick={goBack}
                        disabled={isSubmitting}
                      >
                        ← Previous
                      </button>
                    )}
                    <div className="form-nav-spacer" />
                    {currentStep < 3 ? (
                      <button
                        type="button"
                        className="form-btn-next"
                        onClick={goNext}
                      >
                        Continue →
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="form-btn-submit"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="btn-loading">
                            <span className="spinner" />
                            Submitting…
                          </span>
                        ) : (
                          "🚀 Submit Function"
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Live Preview Sidebar */}
                <aside className="form-preview">
                  <div className="preview-card">
                    <div className="preview-header">
                      <span className="preview-dot green" />
                      <span className="preview-dot yellow" />
                      <span className="preview-dot red" />
                      <span className="preview-title">Live Preview</span>
                    </div>
                    <div className="preview-body">
                      <div className="preview-completion">
                        <div className="completion-ring">
                          <svg viewBox="0 0 36 36" className="circular-chart">
                            <path
                              className="circle-bg"
                              d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                              className="circle-fg"
                              strokeDasharray={`${getCompletionPercent()}, 100`}
                              d="M18 2.0845
                                a 15.9155 15.9155 0 0 1 0 31.831
                                a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                          </svg>
                          <span className="completion-text">
                            {getCompletionPercent()}%
                          </span>
                        </div>
                        <span className="completion-label">Complete</span>
                      </div>

                      <div className="preview-fields">
                        <div
                          className={`preview-field ${
                            form.functionName ? "filled" : ""
                          }`}
                        >
                          <span className="pf-label">Function</span>
                          <span className="pf-value">
                            {form.functionName || "—"}
                          </span>
                        </div>
                        <div
                          className={`preview-field ${
                            form.functionType ? "filled" : ""
                          }`}
                        >
                          <span className="pf-label">Type</span>
                          <span className="pf-value">
                            {selectedType
                              ? `${selectedType.icon} ${selectedType.label}`
                              : "—"}
                          </span>
                        </div>
                        <div
                          className={`preview-field ${
                            form.shortName ? "filled" : ""
                          }`}
                        >
                          <span className="pf-label">Short Name</span>
                          <span className="pf-value mono">
                            {form.shortName || "—"}
                          </span>
                        </div>
                        {/* ✅ Year shown as static value */}
                        <div className="preview-field filled">
                          <span className="pf-label">Year</span>
                          <span className="pf-value">2026</span>
                        </div>
                        <div
                          className={`preview-field ${
                            form.adminLogin ? "filled" : ""
                          }`}
                        >
                          <span className="pf-label">Admin</span>
                          <span className="pf-value mono">
                            {form.adminLogin ? `@${form.adminLogin}` : "—"}
                          </span>
                        </div>
                        <div
                          className={`preview-field ${
                            form.businessUseCase ? "filled" : ""
                          }`}
                        >
                          <span className="pf-label">Use Case</span>
                          <span className="pf-value truncate">
                            {form.businessUseCase
                              ? form.businessUseCase.substring(0, 60) +
                                (form.businessUseCase.length > 60 ? "…" : "")
                              : "—"}
                          </span>
                        </div>
                      </div>

                      <div className="preview-step-indicator">
                        Step {currentStep} of 3
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            )}
          </div>
        </section>

        {/* ─── What Happens Next ─── */}
        <section className="onboard-next-section">
          <h2 className="onboard-section-title centered">
            What Happens <span className="onboard-highlight">Next?</span>
          </h2>
          <p className="onboard-section-subtitle">
            After you submit, here's what our team does
          </p>
          <div className="next-steps-grid">
            <div className="next-step-card">
              <div className="next-step-number">01</div>
              <div className="next-step-icon">🔍</div>
              <h3>Review & Validation</h3>
              <p>
                CIA team reviews your submission for completeness and alignment
                within 2 business days.
              </p>
            </div>
            <div className="next-step-card">
              <div className="next-step-number">02</div>
              <div className="next-step-icon">⚙️</div>
              <h3>Configuration & Setup</h3>
              <p>
                Dashboards, data pipelines, and access permissions are
                configured in the PULSE ecosystem.
              </p>
            </div>
            <div className="next-step-card">
              <div className="next-step-number">03</div>
              <div className="next-step-icon">🧪</div>
              <h3>User Acceptance Testing</h3>
              <p>
                Your team validates the setup in staging. Report issues or
                changes during this phase.
              </p>
            </div>
            <div className="next-step-card">
              <div className="next-step-number">04</div>
              <div className="next-step-icon">🚀</div>
              <h3>Go Live on River Function</h3>
              <p>
                After UAT approval, your function is deployed to production
                with live monitoring.
              </p>
            </div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="onboard-faq-section" id="faq-section">
          <h2 className="onboard-section-title centered">
            Frequently Asked{" "}
            <span className="onboard-highlight">Questions</span>
          </h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                className={`faq-item ${activeFaq === index ? "active" : ""}`}
                key={index}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <span>{faq.question}</span>
                  <span className="faq-toggle">
                    {activeFaq === index ? "−" : "+"}
                  </span>
                </div>
                {activeFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>

      {/* ─── Footer ─── */}
      <footer className="onboard-footer">
        <p>
          © 2025 PULSE — SWOTS CIA Team. Need help?{" "}
          <a href="mailto:cia-team@example.com">Contact CIA Team</a>
        </p>
      </footer>
    </div>
  );
}

export default OnboardPage;